import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Network, Crown, Briefcase, User, ChevronRight, ChevronDown, FileText, Euro, TrendingUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getRoleIcon = (depth) => {
  if (depth === 0) return <Crown className="w-4 h-4 text-amber-500" />;
  if (depth === 1) return <Briefcase className="w-4 h-4 text-sky-500" />;
  return <User className="w-4 h-4 text-emerald-500" />;
};

const getRoleBadge = (depth) => {
  if (depth === 0) return <Badge className="ml-2 bg-amber-100 text-amber-700 text-[10px]">MASTER</Badge>;
  if (depth === 1) return <Badge className="ml-2 bg-sky-100 text-sky-700 text-[10px]">AGENZIA</Badge>;
  return <Badge className="ml-2 bg-emerald-100 text-emerald-700 text-[10px]">SUB-AG.</Badge>;
};

// Flatten the tree into a list of {node, depth, hasChildren} so we can render without recursion.
const flattenTree = (rootNodes, collapsed) => {
  const out = [];
  const stack = [];
  const roots = Array.isArray(rootNodes) ? rootNodes : [rootNodes];
  for (let i = roots.length - 1; i >= 0; i--) {
    if (roots[i]) stack.push({ node: roots[i], depth: 0 });
  }
  while (stack.length) {
    const { node, depth } = stack.pop();
    const children = node.children || [];
    const hasChildren = children.length > 0;
    out.push({ node, depth, hasChildren });
    if (hasChildren && !collapsed[node.id]) {
      for (let i = children.length - 1; i >= 0; i--) {
        stack.push({ node: children[i], depth: depth + 1 });
      }
    }
  }
  return out;
};

const AlberoRete = () => {
  const [tree, setTree] = useState(null);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState({});
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      const [treeRes, contractsRes] = await Promise.all([
        axios.get(`${API_URL}/api/users/my-team/tree`),
        axios.get(`${API_URL}/api/contracts`),
      ]);
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const statsMap = {};
      (contractsRes.data || []).forEach((c) => {
        if (!c.sub_agente_id) return;
        const createdAt = new Date(c.created_at);
        if (createdAt < monthStart) return;
        if (!statsMap[c.sub_agente_id]) statsMap[c.sub_agente_id] = { contratti: 0, attivi: 0, commissioni: 0 };
        statsMap[c.sub_agente_id].contratti += 1;
        if (c.stato === 'attiva' || c.stato === 'accettata') {
          statsMap[c.sub_agente_id].attivi += 1;
          statsMap[c.sub_agente_id].commissioni += parseFloat(c.commissione_maturata || 0);
        }
      });
      setStats(statsMap);
      setTree(treeRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore caricamento albero rete');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleCollapse = (id) => setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  const flat = tree ? flattenTree(tree, collapsed) : [];

  return (
    <div className="space-y-6" data-testid="albero-rete-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
          <Network className="w-6 h-6 text-sky-500" />Albero della Rete
        </h1>
        <p className="text-slate-500 mt-1">Master - Agenzie - Sub-Agenzie - Statistiche del mese corrente</p>
      </div>

      <Card>
        <CardContent className="p-3">
          {flat.length === 0 ? (
            <p className="text-slate-500 text-center py-12">Nessun utente nella rete</p>
          ) : (
            flat.map(({ node, depth, hasChildren }) => {
              const s = stats[node.id] || { contratti: 0, commissioni: 0, attivi: 0 };
              const isCollapsed = !!collapsed[node.id];
              const padLeft = 12 + depth * 24;
              return (
                <div
                  key={node.id}
                  className="flex items-center gap-2 py-2 px-3 hover:bg-slate-50 rounded-md"
                  style={{ paddingLeft: padLeft + 'px' }}
                  data-testid={'tree-node-' + node.id}
                >
                  {hasChildren ? (
                    <button onClick={() => toggleCollapse(node.id)} className="p-0.5 hover:bg-slate-200 rounded">
                      {isCollapsed ? <ChevronRight className="w-3.5 h-3.5 text-slate-500" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-500" />}
                    </button>
                  ) : (
                    <span className="w-4 inline-block" />
                  )}
                  {getRoleIcon(depth)}
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate('/users?focus=' + node.id)}>
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {node.nome} {node.cognome}
                      {getRoleBadge(depth)}
                    </p>
                    <p className="text-xs text-slate-400 truncate">{node.email}</p>
                  </div>
                  <div className="hidden sm:flex items-center gap-4 text-xs text-slate-500">
                    <span className="flex items-center gap-1" title="Contratti mese"><FileText className="w-3 h-3" />{s.contratti}</span>
                    <span className="flex items-center gap-1 text-emerald-600" title="Attivi"><TrendingUp className="w-3 h-3" />{s.attivi}</span>
                    <span className="flex items-center gap-1 tabular-nums" title="Commissioni maturate"><Euro className="w-3 h-3" />{s.commissioni.toFixed(2)}</span>
                  </div>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <p className="text-sm font-semibold text-slate-700 mb-2">Legenda</p>
          <div className="flex flex-wrap gap-4 text-xs text-slate-600">
            <span className="flex items-center gap-1.5"><Crown className="w-4 h-4 text-amber-500" /> Master (super_admin)</span>
            <span className="flex items-center gap-1.5"><Briefcase className="w-4 h-4 text-sky-500" /> Agenzia (1' livello)</span>
            <span className="flex items-center gap-1.5"><User className="w-4 h-4 text-emerald-500" /> Sub-Agenzia (2'+ livello)</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AlberoRete;
